import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { buscarIndicacoes, buscarVouchers, registrarEvento } from '../lib/api';
import { valorConfig } from '../lib/formato';
import { useConfiguracaoPublica, useMe } from '../lib/hooks';
import {
  dispensarOnboarding,
  marcarVouchersVistos,
  onboardingDispensado,
  vouchersVistos,
} from '../lib/armazenamento';
import { PortalShell } from '../componentes/PortalShell';
import { CodigoDestaque } from '../componentes/CodigoDestaque';
import { BotaoIndicar } from '../componentes/BotaoIndicar';
import { TrilhaBrindes } from '../componentes/TrilhaBrindes';
import { ContagemVitalicia } from '../componentes/ContagemVitalicia';
import { CardVoucher } from '../componentes/CardVoucher';
import { ListaIndicacoes } from '../componentes/ListaIndicacoes';
import { CardOnboarding } from '../componentes/CardOnboarding';
import { AvisoCampanha } from '../componentes/AvisoCampanha';
import { Esqueleto, EstadoVazioIndicar, FalhaRede } from '../componentes/blocos';
import { IconePresente, IconeSeta } from '../componentes/icones';

/**
 * Home (UX §5.2) — responde em 5s: "qual é meu código? / o que ganho na próxima?
 * / tem brinde pra retirar?". Hierarquia de cima para baixo, thumb-first.
 * Nenhuma regra de negócio no cliente: nível, próximo brinde e "falta X" vêm de
 * `GET /me`.
 */
export function HomePagina(): ReactNode {
  const me = useMe();
  const config = useConfiguracaoPublica();
  const vouchers = useQuery({ queryKey: ['me', 'vouchers'], queryFn: buscarVouchers });
  const indicacoes = useQuery({ queryKey: ['me', 'indicacoes'], queryFn: buscarIndicacoes });

  const [mostrarOnboarding, setMostrarOnboarding] = useState(false);

  useEffect(() => {
    void registrarEvento('home_visita');
    setMostrarOnboarding(!onboardingDispensado());
  }, []);

  const disponiveis = useMemo(
    () => (vouchers.data?.itens ?? []).filter((v) => v.status === 'disponivel'),
    [vouchers.data],
  );

  // carimbo "voucher novo" (§4.2): ids disponíveis ainda não vistos neste aparelho
  const idsNovos = useMemo(() => {
    const vistos = new Set(vouchersVistos());
    return new Set(disponiveis.filter((v) => !vistos.has(v.id)).map((v) => v.id));
  }, [disponiveis]);

  useEffect(() => {
    if (disponiveis.length) marcarVouchersVistos(disponiveis.map((v) => v.id));
  }, [disponiveis]);

  const brindeIndicado = config.data ? valorConfig(config.data, 'brinde_indicado_descricao') : null;
  const campanhaFim = config.data ? valorConfig(config.data, 'campanha_fim') : null;

  if (me.isLoading) {
    return (
      <PortalShell>
        <div className="flex flex-col gap-8">
          <Esqueleto className="h-24 rounded-3" />
          <Esqueleto className="h-[140px] rounded-2" />
        </div>
      </PortalShell>
    );
  }

  if (me.isError || !me.data) {
    return (
      <PortalShell>
        <FalhaRede aoTentarDeNovo={() => void me.refetch()} />
      </PortalShell>
    );
  }

  const cliente = me.data;
  const codigo = cliente.codigo;

  const dispensar = (): void => {
    dispensarOnboarding();
    setMostrarOnboarding(false);
  };

  return (
    <PortalShell>
      <div className="flex flex-col gap-8">
        {/* 1 — código + CTA (uma unidade: 12px entre eles) */}
        {codigo ? (
          <div className="flex flex-col gap-3">
            <CodigoDestaque
              codigo={codigo}
              aoCopiar={() => void registrarEvento('codigo_copiado')}
            />
            <BotaoIndicar codigo={codigo} brindeIndicado={brindeIndicado} />
          </div>
        ) : (
          <FalhaRede aoTentarDeNovo={() => void me.refetch()} />
        )}

        {/* aviso curto: quem tem voucher disponível sabe já no primeiro quadro
            (auditoria de densidade P2) — nível 1, não duplica o card completo
            do bloco 5, só aponta pra ele. */}
        {disponiveis.length > 0 && (
          <a
            href="#vouchers-para-retirar"
            className="flex min-h-[48px] items-center gap-2 border-b border-linha py-3 tipo-apoio text-tinta"
          >
            <IconePresente width={16} height={16} className="shrink-0 text-tinta-2" />
            <span className="flex-1">
              {disponiveis.length === 1 ? (
                <>
                  Você tem 1 brinde para retirar —{' '}
                  <span className="tipo-corpo-forte text-tinta-forte">
                    {disponiveis[0]?.descricao}
                  </span>
                </>
              ) : (
                `Você tem ${disponiveis.length} brindes para retirar`
              )}
            </span>
            <IconeSeta width={16} height={16} className="shrink-0 text-tinta-2" />
          </a>
        )}

        {/* 2 — onboarding (1ª visita) e frase de regra dizem a mesma coisa:
            nunca as duas juntas (auditoria de densidade P1). */}
        {mostrarOnboarding ? (
          <CardOnboarding aoDispensar={dispensar} />
        ) : (
          <p className="tipo-corpo text-tinta">
            {/* mesmo fallback de `mensagemIndicacao` — nunca inventa o texto do
                brinde, só evita "ganha " colado a nada enquanto a config carrega */}
            Quem usar seu código ganha{' '}
            <span className="tipo-corpo-forte text-tinta-forte">
              {brindeIndicado ?? 'um brinde de boas-vindas'}
            </span>{' '}
            — e você sobe na trilha.
          </p>
        )}

        <AvisoCampanha campanhaFim={campanhaFim} />

        {/* 3 — trilha de brindes */}
        <TrilhaBrindes crescimento={cliente} />

        {/* 4 — contagem vitalícia (só se > 0) */}
        <ContagemVitalicia total={cliente.totalConvertidas} />

        {/* 5 — vouchers para retirar (só se houver) */}
        {disponiveis.length > 0 && (
          <section id="vouchers-para-retirar" className="flex flex-col gap-3">
            <h2 className="tipo-titulo text-tinta-forte">
              {disponiveis.length === 1 ? 'Um brinde para retirar' : 'Brindes para retirar'}
            </h2>
            {disponiveis.map((v) => (
              <CardVoucher key={v.id} voucher={v} novo={idsNovos.has(v.id)} compacto />
            ))}
            <Link
              to="/vouchers"
              className="inline-flex min-h-[48px] items-center gap-1 tipo-apoio text-acento-texto"
            >
              Ver todos os brindes
              <IconeSeta width={16} height={16} />
            </Link>
          </section>
        )}

        {/* 6 — suas indicações */}
        <section className="flex flex-col gap-3">
          <h2 className="tipo-titulo text-tinta-forte">Suas indicações</h2>
          {indicacoes.isLoading && <Esqueleto className="h-24 rounded-2" />}
          {indicacoes.data && indicacoes.data.itens.length === 0 && codigo && (
            <EstadoVazioIndicar codigo={codigo} brindeIndicado={brindeIndicado} />
          )}
          {indicacoes.data && indicacoes.data.itens.length > 0 && (
            <ListaIndicacoes itens={indicacoes.data.itens} limite={3} verTodas />
          )}
        </section>
      </div>
    </PortalShell>
  );
}
